import { cloneDeep } from 'lodash';
import { CellData, CellTypeObject, DateTimeTypeObject } from '../../../../../../../common/table/TableTypes';
import { CellTypeNames } from '../../../../../../../common/table/CellTypeConstants';
import { getDefaultTypeObject } from './tableCellTypeModelUtils';
import { isFormula } from '../formula/tableFormulaPredicateUtils';
import { stringShouldFormatAsType } from '../../../../../../../common/table/utils/tableInputGeneralUtils';
import { getDateFormattingOptions } from '../../../../../../../common/table/utils/tableInputDateTimeUtils';

/**
 * Clears stale auto-inferred type metadata when the latest input no longer matches a known type.
 */
const formatCellAsAutoType = (cellData: CellData): CellData => {
    if (!cellData.type) return cellData;

    const newCellData = cloneDeep(cellData);
    delete newCellData.type;
    return newCellData;
};

/**
 * Returns whether the cell type should change for the inferred value.
 */
export const shouldUpdateCellType = (
    prevCellTypeName: CellTypeNames | undefined,
    newCellTypeName: CellTypeNames,
): boolean => {
    if (prevCellTypeName === newCellTypeName) return false;

    if (!prevCellTypeName) return true;

    // Percentage, Currency, and DateTime are always allowed to replace the current type when inferred from new input.
    if (
        newCellTypeName === CellTypeNames.PERCENTAGE ||
        newCellTypeName === CellTypeNames.CURRENCY ||
        newCellTypeName === CellTypeNames.DATE_TIME
    )
        return true;

    // DateTime can fall back to Number when the edited input no longer looks like a date.
    return prevCellTypeName === CellTypeNames.DATE_TIME && newCellTypeName === CellTypeNames.NUMBER;
};

/**
 * Returns whether the persisted value should change for the inferred type.
 */
const shouldUpdateCellValue = (
    prevCellTypeName: CellTypeNames | undefined,
    newCellTypeName: CellTypeNames,
): boolean => {
    if (prevCellTypeName === newCellTypeName) return true;

    // Only Currency and Percentage can keep their type while the parser falls back to a plain number value.
    if (prevCellTypeName !== CellTypeNames.CURRENCY && prevCellTypeName !== CellTypeNames.PERCENTAGE) return false;

    // Currency and Percentage cells still rewrite their stored value when the parser strips formatting back to a number.
    return newCellTypeName === CellTypeNames.NUMBER;
};

/**
 * If the user enters some content related to a certain cell type (such as
 * a currency symbol in front of a number), update the cell type to match and return the number value
 */
export const applyInferredCellType = (
    cellData: CellData,
    oldCellValue: string | null = null,
    locale: string,
    currencyPreference?: string,
): CellData => {
    if (cellData.type?.name === CellTypeNames.TEXT || cellData.type?.name === CellTypeNames.CHECKBOX) return cellData;

    // Return if empty or formula result
    if (!cellData.value || isFormula(cellData.value)) return cellData;

    const cellValue = cellData.value || '';

    const formatType = stringShouldFormatAsType(cellValue, true, locale, currencyPreference);
    if (!formatType) return formatCellAsAutoType(cellData);

    const { newCellType, newCellValue, otherTypeOptions } = formatType;

    const shouldUpdateType = shouldUpdateCellType(cellData.type?.name, newCellType);
    const shouldUpdateValue = shouldUpdateType || shouldUpdateCellValue(cellData.type?.name, newCellType);

    if (!shouldUpdateValue && !shouldUpdateType) return cellData;

    const newCellData = cloneDeep(cellData);

    newCellData.value = newCellValue.toString();

    if (shouldUpdateType) newCellData.type = getDefaultTypeObject(newCellType);

    // add any newly calculated type options to the type object, even if the type wasn't updated
    // this is because it's most likely formatting options based on the new input
    newCellData.type = { ...newCellData.type, ...otherTypeOptions } as CellTypeObject;

    if (newCellData.type?.name === CellTypeNames.DATE_TIME) {
        const dateTypeOptions = getDateFormattingOptions(newCellValue, newCellData.type, {
            prevCellValue: oldCellValue,
            prevTypeObject: cellData.type,
            rawInput: cellValue,
            locale,
        });

        newCellData.type = { ...newCellData.type, ...dateTypeOptions } as DateTimeTypeObject;
    }

    return newCellData;
};
